import { transformEnumToDisplay } from '@fruitful/fruitils';
import { TaxFiling } from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useCallback, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Form,
    ScreenContainer,
    SelectSingle,
    Spinner,
    USE_FORM_MODE_DEFAULTS,
    useToastController,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../logger/Logger';
import useUpdateUser from '../../user/hooks/useUpdateUser';
import { useUser } from '../../user/providers/UserProvider';
import { useTrackFinancialProfileCompletion } from '../hooks/useTrackFinancialProfileCompletion';
import { getMemberProfileUserSubtitle } from '../utils/memberProfileUtils';
import {
    SUCCESS_TOAST_DURATION,
    SUCCESS_TOAST_MESSAGE,
    SuccessToastIcon,
} from './SuccessToast';

const taxesSchema = z.object({
    taxFiling: z.nativeEnum(TaxFiling, {
        required_error: 'Please select a tax filing status',
    }),
});

type TaxesSchema = z.infer<typeof taxesSchema>;

export const Taxes = () => {
    LogRocketFacade.tagPage('Profile/Taxes');

    const toast = useToastController();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const authedUser = useAuthedUser();
    const { userId } = useLocalSearchParams<{ userId?: string }>();

    // Use provided userId or fallback to authenticated user. Find the target user.
    const targetUserId = userId ?? authedUser.id;
    const targetUser = [authedUser, authedUser.partnerMember].find(
        (user) => user?.id === targetUserId,
    );

    const { refetch: refetchUser } = useUser();
    const { mutateAsync: updateUser, isPending: isUpdatingUser } =
        useUpdateUser();

    const { trackFinancialProfileUpdate } =
        useTrackFinancialProfileCompletion();

    const {
        handleSubmit,
        formState: { isValid },
        setValue,
        watch,
    } = useForm<TaxesSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(taxesSchema),
        defaultValues: {
            taxFiling: targetUser?.taxFiling ?? undefined,
        },
    });

    const watchedTaxFiling = watch('taxFiling');

    const taxFilingOptions = Object.values(TaxFiling).map((filing) => ({
        title: transformEnumToDisplay(filing),
    }));

    const onSubmit: SubmitHandler<TaxesSchema> = useCallback(
        async (data) => {
            if (targetUser == null) {
                captureException(
                    new Error('Target user not found for tax filing update'),
                );
                return;
            }

            setIsSubmitting(true);

            try {
                await updateUser({
                    urlParams: { userId: targetUserId },
                    body: {
                        taxFiling: data.taxFiling,
                    },
                });

                void refetchUser?.();

                toast.show(SUCCESS_TOAST_MESSAGE, {
                    duration: SUCCESS_TOAST_DURATION,
                    icon: SuccessToastIcon,
                });

                trackFinancialProfileUpdate({
                    taxesUpdated: data.taxFiling != null,
                });

                router.back();
            } catch (error) {
                captureException(error);
                toast.show('Failed to update tax filing status', {
                    duration: 2000,
                });
            } finally {
                setIsSubmitting(false);
            }
        },
        [
            targetUser,
            updateUser,
            refetchUser,
            toast,
            trackFinancialProfileUpdate,
        ],
    );

    const handleTaxFilingChange = (index: number) => {
        const selectedFiling = Object.values(TaxFiling)[index];
        if (selectedFiling != null) {
            setValue('taxFiling', selectedFiling, { shouldValidate: true });
        }
    };

    const isFormSubmitting = isSubmitting || isUpdatingUser;

    // Should not happen, but added as a defensive check.
    if (targetUser == null) {
        captureException(new Error('Target user not found for taxes page'));
        return <Redirect href="/profile/financial-profile" />;
    }

    return (
        <ScreenContainer
            title="Tax filing status"
            subtitle={getMemberProfileUserSubtitle(authedUser, targetUser)}
            hasBackButton={true}
        >
            {isFormSubmitting ? (
                <Spinner />
            ) : (
                <YStack gap={24} justifyContent="space-between">
                    <YStack gap={16}>
                        <YStack gap={8}>
                            <BodyMediumRegular>
                                What is your current tax filing status?
                            </BodyMediumRegular>
                        </YStack>

                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <SelectSingle
                                options={taxFilingOptions}
                                selectedIndex={Object.values(
                                    TaxFiling,
                                ).findIndex(
                                    (filing) => filing === watchedTaxFiling,
                                )}
                                onSelect={handleTaxFilingChange}
                            />
                        </Form>
                    </YStack>

                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        onPress={handleSubmit(onSubmit)}
                        disabled={!isValid || isFormSubmitting}
                    />
                </YStack>
            )}
        </ScreenContainer>
    );
};
